import type { ReactNode } from 'react'

import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

type BarCellProps = {
  value: ReactNode
  /** Fill width as a share of the column's largest row, 0–100. */
  sharePct: number
  barClassName?: string
  className?: string
}

/**
 * A grid cell whose number sits above a proportional bar scaled to the largest
 * value in its column.
 *
 * A column of right-aligned figures is precise but not comparable — the reader
 * has to hold three numbers in their head to rank them. The bar makes the
 * ranking pre-attentive while the figure keeps the precision, which is the
 * whole job of a comparison table.
 *
 * The bar is `aria-hidden`: it encodes nothing the adjacent text does not.
 */
export const BarCell = ({ value, sharePct, barClassName, className }: BarCellProps) => (
  <div className={cn('flex w-full flex-col items-end gap-1', className)}>
    <Typography
      className="tabular-nums leading-100"
      variant="body2"
    >
      {value}
    </Typography>
    {/* Capped and right-aligned so the track reads as one unit with the figure
        above it. Left to fill the cell, a wide money column stretches the bar
        far past its own number and the pair stops looking related. */}
    <div
      aria-hidden
      className="h-1 w-full max-w-[120px] overflow-hidden rounded-full bg-paper-elevation4"
    >
      <div
        className={cn('h-full rounded-full bg-text-secondary', barClassName)}
        style={{ width: `${Math.min(100, Math.max(0, sharePct))}%` }}
      />
    </div>
  </div>
)
